/**
 * Product Demand Component
 * 
 * Displays demand generation metrics with a vertical bar chart:
 * - Customer pipeline: leads → prospects → contracts → sales
 * - CAC (Customer Acquisition Cost)
 * - Lead time
 */

import React from 'react';
import { Tag, Tooltip } from 'antd';
import {
  FunnelPlotOutlined,
  DollarOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { BaseReportingComponent, ComponentData, ReportingComponentProps } from './BaseReportingComponent';
import { ProcessStepsChart, ProcessStep } from './ProcessStepsChart';
import { fetchProductDomainDashboardComponent } from '../../../services/bridgeSessionDataAdapter';
import { isDriveFlowReportingEnabled } from '../../../services/driveFlowAdapter';
import styles from './ProductDemandComponent.module.css';

interface ProductDemandComponentProps extends Omit<ReportingComponentProps, 'componentType'> {
  /** Whether to use compact view */
  compact?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDuration = (days: number): string => {
  if (days < 1) return '< 1 day';
  if (days === 1) return '1 day';
  if (days < 7) return `${days} days`;
  if (days < 30) return `${Math.round(days / 7)} weeks`;
  return `${Math.round(days / 30)} months`;
};

const getDemandSteps = (data: Record<string, any>): ProcessStep[] => {
  if (Array.isArray(data.funnel_steps) && data.funnel_steps.length > 0) {
    return data.funnel_steps.map((s: { id: string; name: string; count: number; color?: string }) => ({
      id: s.id,
      name: s.name,
      count: s.count ?? 0,
      color: s.color ?? '#3b82f6',
    }));
  }
  return [
    {
      id: 'leads',
      name: 'Leads',
      count: data.customer_leads ?? data.total_leads ?? 0,
      color: '#60a5fa'
    },
    {
      id: 'prospects',
      name: 'Prospects',
      count: data.customer_prospects ?? data.total_prospects ?? 0,
      color: '#3b82f6'
    },
    {
      id: 'contracts',
      name: 'Contracts',
      count: data.customer_contracts ?? data.total_contracts ?? 0,
      color: '#2563eb'
    },
    {
      id: 'sales',
      name: 'Sales',
      count: data.customer_sales ?? data.total_sales ?? 0,
      color: '#10b981'
    }
  ];
};

export const ProductDemandComponent: React.FC<ProductDemandComponentProps> = ({
  compact = false,
  ...props
}) => {
  const renderContent = (data: ComponentData, loading: boolean) => {
    const steps = getDemandSteps(data.data);
    
    const leads = steps[0]?.count || 0;
    const sales = steps[steps.length - 1]?.count || 0;
    const overallConversion = leads > 0 ? Math.round((sales / leads) * 100) : 0;

    const cac = data.data.cac ?? 0;
    const leadTime = data.data.lead_time ?? data.data.demand_lead_time ?? 0;

    return (
      <div className={`${styles.productDemand} ${compact ? styles.compact : ''}`}>
        {!compact && (
          <div className={styles.header}>
            <h4 className={styles.title}>
              <FunnelPlotOutlined /> Demand Pipeline
            </h4>
          </div>
        )}

        {/* Bar Chart */}
        <div className={styles.chartWrapper}>
          <ProcessStepsChart
            steps={steps}
            height={compact ? 140 : 180}
            barColor="#3b82f6"
            showLabels={true}
            showConversionRates={!compact}
            compact={compact}
          />
        </div>

        {/* Key Metrics */}
        <div className={styles.keyMetrics}>
          <Tooltip title="Customer Acquisition Cost">
            <div className={styles.keyMetric}>
              <DollarOutlined className={styles.metricIcon} />
              <div className={styles.metricContent}>
                <span className={styles.metricLabel}>CAC</span>
                <span className={styles.metricValue}>{formatCurrency(cac)}</span>
              </div>
            </div>
          </Tooltip>
          
          <Tooltip title="Average time from lead to sale">
            <div className={styles.keyMetric}>
              <ClockCircleOutlined className={styles.metricIcon} />
              <div className={styles.metricContent}>
                <span className={styles.metricLabel}>Lead Time</span>
                <span className={styles.metricValue}>{formatDuration(leadTime)}</span>
              </div>
            </div>
          </Tooltip>
          
          <Tooltip title="Overall conversion: Leads to Sales">
            <div className={styles.keyMetric}>
              <FunnelPlotOutlined className={styles.metricIcon} />
              <div className={styles.metricContent}>
                <span className={styles.metricLabel}>Conversion</span>
                <Tag color={overallConversion >= 10 ? 'green' : overallConversion >= 5 ? 'orange' : 'red'}>
                  {overallConversion}%
                </Tag>
              </div>
            </div>
          </Tooltip>
        </div>
      </div>
    );
  };

  const customDataFetcher = isDriveFlowReportingEnabled()
    ? (id: string) => fetchProductDomainDashboardComponent(id, 'product_demand')
    : undefined;

  return (
    <BaseReportingComponent
      {...props}
      componentType="product_demand"
      showControls={!compact}
      customDataFetcher={customDataFetcher}
    >
      {renderContent}
    </BaseReportingComponent>
  );
};

export default ProductDemandComponent;
